import React, { Fragment, useEffect, useState } from "react";
import "@fortawesome/fontawesome-free-webfonts";
import "@fortawesome/fontawesome-free-webfonts/css/fa-solid.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { IntlProvider } from "react-intl";
import { useSelector } from "react-redux";
import GoogleTagManager from "./GoogleTagManager";
import Subscription from "landing/components/Subscription";
import type { LandingPageConfig } from "landing/objects/LandingPageConfig";
import type { IMainAppState } from "main/reducers/IMainAppState";

const LOADED_LANGUAGE = Symbol("LOADED_LANGUAGE");

export default () => {
  const languageCode = useSelector(
    (state: IMainAppState) => state.app.languageCode,
  );
  const templateId = useSelector(
    (state: IMainAppState) => state.app.templateId,
  );
  const [landingPageConfig, setLandingPageConfig] =
    useState<LandingPageConfig | null>(null);

  const [messages, setMessages] = useState<{} | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const languageFilesPromises: Array<
        Promise<{ default: Record<string, string> }>
      > = [];
      // Load base languages
      languageFilesPromises.push(import(`../../i18n/${languageCode}`));
      // Load language file with country code
      if (landingPageConfig?.countryCode) {
        languageFilesPromises.push(
          import(
            `../../i18n/${languageCode}-${landingPageConfig.countryCode}`
          ).catch(() => {
            // It is ok not to have template specific messages
          }),
        );
      }
      // Load template languages (optional)
      languageFilesPromises.push(
        import(`../../templates/i18n/${languageCode}-${templateId}`).catch(
          () => {
            // It is ok not to have template specific messages
          },
        ),
      );

      const messageImports = await Promise.all(languageFilesPromises);
      const messages = messageImports.reduce((messages, messageImport) => {
        if (messageImport !== undefined) {
          Object.assign(messages, messageImport.default);
        }
        return messages;
      }, {});

      messages[LOADED_LANGUAGE] = languageCode + templateId;
      setMessages(messages);
    })();
  }, [landingPageConfig?.countryCode, languageCode, templateId]);

  // If the basic messages are not loaded, do not show anything.
  if (!messages) {
    return null;
  }

  return (
    <IntlProvider
      locale={languageCode}
      messages={messages}
      textComponent={Fragment}
    >
      <div className="app">
        <div className="container mt-3">
          <Subscription
            loadedLanguage={messages[LOADED_LANGUAGE]}
            onChangeLandingPageConfig={setLandingPageConfig}
          />
        </div>
      </div>

      <GoogleTagManager />
    </IntlProvider>
  );
};
